import React, { FormEvent } from 'react'
import { graphql } from 'react-apollo'

import {
  BackButton,
  Button,
  ConfirmationModal,
  ModalActionTypes,
  TextInput,
} from '@acorns/web-components'
import { AnalyticsClient, withAnalyticsClient } from '@acorns/web-utils'
import { FormikActions, withFormik } from 'formik'
import { compose, lifecycle, withHandlers, withState } from 'recompose'
import styled from 'styled-components'

import { emailToPrefill } from 'pages/login/form'
import {
  logInForgotPasswordBack,
  logInForgotPasswordNext,
  logInForgotPasswordViewed,
} from 'utils/segment'
import withAnalyticsIdentity from 'utils/with-analytics-identity'

import Layout from '../../components/Layout'
import { theme } from '../../theme'
import {
  Values,
  handleFormSubmit,
  mapPropsToValues,
  validationSchema,
} from './form'
import { forgotPassword } from './mutation'

export type Props = {
  children: any
  values: Values
  isSubmitting: boolean
  isValid: boolean
  isModalOpen: boolean
  setIsModalOpen: (value: boolean) => void
  onBackPress: () => void
  handleModal: () => void
  handleContactSupport: () => void
  handleChange: (event: FormEvent<HTMLInputElement>) => void
  submitForm: () => Promise<any>
  handleReset: () => void
  handleBlur: (event: FormEvent<HTMLInputElement>) => void
  reset: (options?: any) => any
  analytics: AnalyticsClient
} & FormikActions<{ email: string }>

const BackButtonContainer = styled.div`
  width: 100%;
  position: absolute;
  top: -65px;
  left: 0;
`

const BackButtonStyled = styled(BackButton)`
  @media screen and (max-width: ${theme('breakpoints.sm')}px) {
    left: 0;
    position: absolute;
  }
`

const SubmitButton = styled(Button)`
  width: 100%;
`

export const enhance = compose(
  withAnalyticsClient,
  withAnalyticsIdentity,
  graphql(forgotPassword, { name: 'reset' }),
  withState('isModalOpen', 'setIsModalOpen', false),

  withFormik({
    validationSchema,
    mapPropsToValues,
    handleSubmit: handleFormSubmit,
  }),
  withHandlers({
    onBackPress:
      ({ history, analytics }) =>
      () => {
        analytics.track('Button Tapped', logInForgotPasswordBack)
        const url = document.referrer.split('/')
        if (url[3] === 'redeem') {
          history.push('/redeem')
        } else {
          history.push('/')
        }
      },
    handleModal:
      ({ isModalOpen, setIsModalOpen, history, analytics }) =>
      () => {
        analytics.track('Button Tapped', logInForgotPasswordNext)
        if (isModalOpen) {
          setIsModalOpen(false)
          history.push('/')
        } else {
          setIsModalOpen(true)
        }
      },
    handleContactSupport:
      ({ setIsModalOpen }) =>
      () => {
        setIsModalOpen(false)
        window.location.href = 'https://help.acorns.com/contact'
      },
  }),
  withHandlers({
    handleReset:
      ({ handleModal, submitForm }) =>
      (event) => {
        event.preventDefault()
        submitForm().then(handleModal)
      },
  }),
  lifecycle<Props, {}>({
    componentDidMount() {
      this.props.analytics.track('Screen Viewed', logInForgotPasswordViewed)
    },
  }),
)

export const ForgotPassword = ({
  onBackPress,
  handleReset,
  handleChange,
  handleBlur,
  values,
  isSubmitting,
  isValid,
  isModalOpen,
  handleModal,
  handleContactSupport,
}: Props) => (
  <Layout
    copy="Forgot Password"
    subcopy="Please enter your email address. Reset instructions will be sent to you."
  >
    <BackButtonContainer>
      <BackButtonStyled onClick={onBackPress} style={{ marginRight: '16px' }} />
    </BackButtonContainer>

    <form onSubmit={handleReset}>
      <TextInput
        id="email"
        label="Email"
        name="email"
        onBlur={handleBlur}
        onChange={handleChange}
        placeholder={'name@acorns.com'}
        type="email"
        value={values.email}
      />

      <SubmitButton
        disabled={isSubmitting || (!emailToPrefill && !isValid)}
        onPress={handleReset}
      >
        Reset Password
      </SubmitButton>
    </form>

    <ConfirmationModal
      isOpen={isModalOpen}
      actions={[
        {
          handler: handleContactSupport,
          label: 'Contact Support',
          type: ModalActionTypes.secondary,
        },
        {
          handler: handleModal,
          label: 'Got it!',
          type: ModalActionTypes.primary,
        },
      ]}
      title="Forgot Password"
      message="If our records match the email provided, you'll receive an email with reset instructions. If you do not receive an email in the next few minutes, please try again."
    />
  </Layout>
)

export default enhance(ForgotPassword)
